import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import theme from 'fe-appdir-lib-theme';
import { StatelessDrawer } from 'fe-website-comp-drawer';
import SearchInput from './SearchInput';
import SearchResults from './SearchResults';
import MagnifyingGlass from './MagnifyingGlass';
import { Backdrop } from 'fe-appdir-comp-modal';
import { connect } from 'fe-hoc-connect';
import {
  store,
  hasSearchResults,
  toggleIsUserSearching,
} from 'fe-appdir-data-app';

const PROP_TYPES = {
  resultsVisible: PropTypes.bool.isRequired,
  searchQuery: PropTypes.string,
  isSearchPage: PropTypes.bool,
  onSubmit: PropTypes.func,
  onResultClick: PropTypes.func,
  gaTrackIds: PropTypes.object,
  logCustomEvent: PropTypes.func,
};

const DEFAULT_PROPS = {
  gaTrackIds: {},
};

const DesktopContainer = styled.div`
  position: relative;

  @media (max-width: ${theme.breakpointSm}) {
    display: none;
  }
`;

// Display flex needed to vertically align svg icon
const MobileContainer = styled.div`
  display: flex;
  position: relative;

  @media (min-width: ${theme.breakpointSm}) {
    display: none;
  }
`;

const MobileSearchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 100%;
`;

class UnconnectedSearchBar extends React.Component {
  state = {
    isMobileDrawerOpen: false,
  };

  toggleMobileDrawer = () => {
    this.setState({
      isMobileDrawerOpen: !this.state.isMobileDrawerOpen,
    });
  };

  onMobileResultClick = () => {
    this.props.onResultClick();
    this.toggleMobileDrawer();
  };

  /* Analytics callbacks for custom event tracking */

  gaEngagedCallback = (query) => {
    // Unlike the other two, this callback needs to be passed the query (from event.target.value)
    // because it is called in onChange, so props.searchQuery may not be updated yet
    this.props.logCustomEvent(this.props.gaTrackIds.SEARCH_ENGAGED, query);
  };

  gaSubmitCallback = () => {
    this.props.logCustomEvent(
      this.props.gaTrackIds.SEARCH_SUBMIT,
      this.props.searchQuery,
    );
  };

  gaResultClickCallback = (slug) => {
    const {
      SEARCH_SELECT_DROPDOWN_QUERY,
      SEARCH_SELECT_DROPDOWN_APP_BY_QUERY,
    } = this.props.gaTrackIds;

    if (SEARCH_SELECT_DROPDOWN_QUERY)
      this.props.logCustomEvent(
        SEARCH_SELECT_DROPDOWN_QUERY,
        this.props.searchQuery,
      );

    if (SEARCH_SELECT_DROPDOWN_APP_BY_QUERY)
      this.props.logCustomEvent(
        SEARCH_SELECT_DROPDOWN_APP_BY_QUERY + '_' + this.props.searchQuery,
        slug,
      );
  };

  render() {
    const { resultsVisible, onSubmit, onResultClick, isSearchPage } = this.props;
    return (
      <>
        <DesktopContainer>
          <SearchInput
            onSubmit={onSubmit}
            gaEngagedCallback={this.gaEngagedCallback}
            gaSubmitCallback={this.gaSubmitCallback}
          />
          <SearchResults
            isVisible={resultsVisible && !isSearchPage}
            onResultClick={onResultClick}
            gaResultClickCallback={this.gaResultClickCallback}
          />
          <Backdrop
            isOpen={resultsVisible && !isSearchPage}
            onClick={() => toggleIsUserSearching()}
            isTransparent
          />
        </DesktopContainer>

        <MobileContainer>
          <StatelessDrawer
            icon={MagnifyingGlass}
            isOpen={this.state.isMobileDrawerOpen}
            onClick={this.toggleMobileDrawer}
          >
            <MobileSearchWrapper>
              <SearchInput
                onSubmit={onSubmit}
                gaEngagedCallback={this.gaEngagedCallback}
                gaSubmitCallback={this.gaSubmitCallback}
              />
              <SearchResults
                isVisible={resultsVisible}
                onResultClick={this.onMobileResultClick}
                gaResultClickCallback={this.gaResultClickCallback}
              />
            </MobileSearchWrapper>
          </StatelessDrawer>
        </MobileContainer>
      </>
    );
  }
}

UnconnectedSearchBar.propTypes = PROP_TYPES;
UnconnectedSearchBar.defaultProps = DEFAULT_PROPS;
UnconnectedSearchBar.displayName = 'SearchBar';

const SearchBar = connect(
  store,
  (state) => ({
    isSearchPage: typeof window !== 'undefined' && window.location ? window.location.pathname === '/search' : false,
    // Hide SearchResults dropdown if there are no results, if the user cleared the SearchInput, or if the input is not focused
    resultsVisible:
      hasSearchResults(state) &&
      state.searchQuery !== '' &&
      state.isUserSearching,
    // For analytics only
    searchQuery: state.searchQuery,
  }),
)(UnconnectedSearchBar);

export default SearchBar;
